import { Box, Button, Grid2, Modal, Stack, TextField, Typography } from "@mui/material";
import '@uppy/core/dist/style.min.css';
import '@uppy/dashboard/dist/style.min.css';
import Uppy from "@uppy/core";
import { Dashboard } from "@uppy/react";
import { useAtom } from "jotai";
import { useState } from "react";
import { toast } from "react-toastify";
import { addFloorModalOpenAtom, newProjectFloorsAtom, NewProjectModalFloorInput, uploadProgressAtom } from "store/atoms/NewProject/newProjectAtoms";
import AwsS3 from '@uppy/aws-s3';
import CONFIG from "utils/config";
import { Floor } from "utils/floor";
import { useParams } from "react-router-dom";
import { useGetProjectQuery } from "api/project/queries/useGetProjectQuery";
import FloorCard from "./FloorCard";

const NEW_FLOORPLAN_SPACING_IN_Z = 1;

const AddFloors = () => {
  const { projectSlug = "" } = useParams();
  const { data: project } = useGetProjectQuery(projectSlug);

  const [isModalOpen, setIsModalOpen] = useAtom(addFloorModalOpenAtom);
  const [floors, setFloors] = useAtom(newProjectFloorsAtom);
  const [, setUploadProgress] = useAtom(uploadProgressAtom);
  const [selectedFile, setSelectedFile] = useState<{
    s3Name: string;
    dimensions: {
      width: number;
      length: number;
    }
  } | undefined>();
  const [floorName, setFloorName] = useState("");
  const [uppy] = useState(() => new Uppy().use(AwsS3, {
    endpoint: `${CONFIG.TRANSFERS_API_URL}/companion`,
    shouldUseMultipart: true,
  }));

  uppy.setOptions({
    restrictions: {
      allowedFileTypes: ['image/*'],
      maxNumberOfFiles: 1,
    }
  });

  uppy.on("upload-progress", (file, progress) => {
    const { bytesUploaded, bytesTotal } = progress;
    if (!bytesTotal) return;
    const progressPercentage = (bytesUploaded / bytesTotal) * 100;
    
    setUploadProgress(prev => 
      prev.map(p => 
        p.fileName === file?.name 
          ? { ...p, progress: progressPercentage } 
          : p
      )
    );
  });

  uppy.on("file-added", (file) => {
    file.name = file.name ? file.name.replace(/\s+/g, "-") : file.id;
    file.meta.s3Key = `projects/${project?.id}/floorplans/${file.name}`;
    const newImage = new Image();
    newImage.src = file.preview || URL.createObjectURL(file.data);
    newImage.onload = () => {
      setSelectedFile({
        s3Name: file.name || file.id,
        dimensions: {
          width: newImage.width,
          length: newImage.height
        }
      });
    }
  });

  uppy.on("file-removed", (file) => {
    setSelectedFile(undefined);
    setUploadProgress(prev => prev.filter(p => p.fileName !== file.name));
  });

  const createDisabled = Boolean(!selectedFile || !floorName || floors.some((floor) => floor.name === floorName));

  const createFloor = () => {
    if (!selectedFile || !floorName) return;

    for (const floor of floors) {
      if (floor.name === floorName) {
        toast.error("Floor name already exists");
        return;
      }
    }

    let maxHeight = floors[0]?.position.z;
    for (const floor of floors) {
      maxHeight = maxHeight === undefined ? 0 : Math.max(maxHeight, floor.position.z);
    }
    const newHeight = maxHeight === undefined ? 0 : maxHeight + NEW_FLOORPLAN_SPACING_IN_Z;

    const newFloor: NewProjectModalFloorInput = {
      name: floorName,
      s3Url: selectedFile.s3Name,
      position: { x: 0, y: 0, z: newHeight },
      metadata: {
        floorplan: {
          width: selectedFile.dimensions.width,
          height: selectedFile.dimensions.length
        }
      }
    };

    setFloors([...floors, newFloor]);
    setIsModalOpen(false);
    setSelectedFile(undefined);
    setFloorName("");
    uppy.clear();
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">Floors ({floors.length})</Typography>
        <Button variant="contained" onClick={() => setIsModalOpen(true)}>
          Add Floor
        </Button>
      </Stack>

      {floors.length > 0 && (
            <Grid2 container spacing={3} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
              {floors?.map(floor => ({
                ...floor,
                image: uppy.getFiles()[0]?.preview || ""
              })).map((floor) => (
                <FloorCard floor={floor} key={floor.name} />
              ))}
            </Grid2>
      )}

      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedFile(undefined);
          setFloorName("");
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 600,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 1,
          }}
        >
          <Stack spacing={3}>
            <Typography variant="h6">Add New Floor</Typography>
            
            <TextField
              label="Floor Name"
              value={floorName}
              onChange={(e) => setFloorName(e.target.value)}
              error={floors.some((floor) => floor.name === floorName)}
              helperText={floors.some((floor) => floor.name === floorName) ? "Floor name already exists" : ""}
            />

            <Box>
              <Typography variant="subtitle2" gutterBottom>Upload Floorplan</Typography>
              <Dashboard
                uppy={uppy}
            theme="dark"
            proudlyDisplayPoweredByUppy={false}
            // hideUploadButton
            width={"inherit"}
            // height={"inherit"}
            style={{
              background: "#212121",
              width: "inherit",
              maxWidth: "inherit",
              height: "inherit",
              overflow: "hidden",
            }}
              />
            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => {
                  setIsModalOpen(false);
                  setSelectedFile(undefined);
                  setFloorName("");
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={createFloor}
                disabled={createDisabled}
              >
                Create Floor
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </Stack>
  );
};

export default AddFloors;
